import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { z } from "zod";

import { CostCenterAutocompleteInput } from "@/components/shared/form/cost-center-autocomplete-input";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { Form } from "@/components/ui/form";
import { useTenant } from "@/providers/session";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

import { AccountAutocompleteInput } from "@/components/shared/form/account-autocomplete-input";
import { TaxCodeAutocompleteInput } from "@/components/shared/form/tax-code-autocomplete-input";
import { FormNavigation } from "../form/shared/form-navigation";

const defaultValues = {
  accountId: undefined,
  costCenterId: undefined,
  taxCodeId: undefined,
};

const editLineItemFormSchema = z.object({
  accountId: z.string().max(255).nullable().optional(),
  taxCodeId: z.string().max(255).nullable().optional(),
  costCenterId: z.string().max(255).nullable().optional(),
});

type EditLineItemFrom = z.infer<typeof editLineItemFormSchema>;

type Props = {
  onSubmit: (data: EditLineItemFrom) => Promise<void>;
  initialValues?: EditLineItemFrom;
};

export const EditBookingForm = ({
  onSubmit,
  initialValues = defaultValues,
}: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { accounts } = useGetAllAccounts();
  const { taxCodes } = useGetAllTaxCodes();
  const { costCenters } = useGetAllCostCenters();
  const { hasCostCenters } = useTenant();

  const form = useForm<EditLineItemFrom>({
    resolver: zodResolver(editLineItemFormSchema),
    defaultValues,
    values: initialValues,
  });

  const handleFormFocus = useCallback(() => {
    const firstInput = formRef.current?.querySelector("input");
    if (firstInput) {
      firstInput.focus();
    }
  }, []);

  const handleSubmit = async ({
    accountId,
    taxCodeId,
    costCenterId,
  }: EditLineItemFrom) => {
    await onSubmit({
      accountId,
      taxCodeId,
      costCenterId,
    });
  };

  useHotkeys(["shift+equal", "+", "BracketRight", "NumpadAdd"], () => {
    setTimeout(() => {
      handleFormFocus();
    }, 100);
  });

  return (
    <Form {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex items-center gap-2 w-full"
      >
        <FormNavigation loop className="flex-1">
          {({ onKeyDown }) => {
            return (
              <div className="flex flex-1 items-start gap-2 w-full">
                <TaxCodeAutocompleteInput
                  name="taxCodeId"
                  placeholder="BU"
                  options={taxCodes ?? []}
                  onKeyDown={onKeyDown}
                  showEmptyLabel
                  side="top"
                />
                <AccountAutocompleteInput
                  name="accountId"
                  placeholder="Konto"
                  options={accounts ?? []}
                  showEmptyLabel
                  onKeyDown={onKeyDown}
                  side="top"
                />
                {hasCostCenters && (
                  <CostCenterAutocompleteInput
                    name="costCenterId"
                    placeholder="Kostenstelle"
                    options={costCenters ?? []}
                    showEmptyLabel
                    onKeyDown={onKeyDown}
                    side="top"
                  />
                )}
                <AutoSaveForm onSubmit={handleSubmit} debounceTime={100} />
              </div>
            );
          }}
        </FormNavigation>
      </form>
    </Form>
  );
};
